// Sign-in and session rules (PLAN.md 4.2). Pure: no database, no clock, no network.

/** How a person signs in: Google for the company's Google domain, a one-time email code otherwise. */
export type SignInMethod = "google" | "code";
/** Where an identity came from, as stored in user_identities.provider. */
export type IdentityProvider = "google" | "auth0";

export const IDLE_LIMIT_MS = 12 * 60 * 60 * 1000;
export const ABSOLUTE_LIMIT_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * last_seen_at (the last activity) is written at most this often, so a busy screen doesn't write
 * on every click. Short, because the idle sign-out reads it (brief 1E-19c).
 */
export const SEEN_EVERY_MS = 15 * 1000;

const EMAIL = /^[^\s@]+@([a-z0-9-]+\.)+[a-z]{2,}$/;
const DOMAIN = /^([a-z0-9-]+\.)+[a-z]{2,}$/;

/** Trimmed and lower-case, or null when it isn't an email address. */
export function normalizeEmail(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const email = input.trim().toLowerCase();
  return email.length <= 254 && EMAIL.test(email) ? email : null;
}

/** An address at the Google domain goes to Google; any other gets a one-time code. */
export function signInMethod(email: string, googleDomain: string): SignInMethod {
  return email.endsWith(`@${googleDomain}`) ? "google" : "code";
}

/** The identity provider a method produces. */
export function providerFor(method: SignInMethod): IdentityProvider {
  return method === "google" ? "google" : "auth0";
}

export interface ProvenIdentity {
  provider: IdentityProvider;
  subject: string;
  email: string;
  emailVerified: boolean;
  /** Google Workspace's hosted-domain claim (`hd`); absent for personal Google accounts. */
  hostedDomain?: string | null;
}

/**
 * Checks what came back from the sign-in provider. A Google-domain address must have come
 * through Google, carrying Google Workspace's hosted-domain claim for that same domain, and any
 * other address through the email code; the email must be verified.
 * Returns why the identity is refused, or null.
 */
export function refuseIdentity(identity: ProvenIdentity, googleDomain: string): string | null {
  const email = normalizeEmail(identity.email);
  if (!email || !identity.emailVerified) return "email not verified";
  if (!identity.subject.trim()) return "no subject";
  if (providerFor(signInMethod(email, googleDomain)) !== identity.provider)
    return "signed in the wrong way for this email";
  if (identity.provider === "google" && identity.hostedDomain?.toLowerCase() !== googleDomain)
    return "hosted domain doesn't match";
  return null;
}

export interface SessionState {
  last_seen_at: Date;
  expires_at: Date;
  ended_at: Date | null;
}

/**
 * A session is refused when it was ended (signed out), passed its absolute end (7 days),
 * sat idle longer than 12 hours, or belongs to a user who is switched off or gone.
 * Returns why, or null when the request may go ahead.
 */
export function refuseSession(
  session: SessionState,
  user: { status: string } | null,
  now: Date,
  idleLimitMs?: number,
): string | null {
  if (session.ended_at) return "session ended";
  if (now.getTime() >= session.expires_at.getTime()) return "session expired";
  if (now.getTime() - session.last_seen_at.getTime() > IDLE_LIMIT_MS) return "session idle";
  // The company's idle sign-out (brief 1E-19c), with or without a tab open.
  if (idleLimitMs !== undefined && now.getTime() - session.last_seen_at.getTime() > idleLimitMs)
    return IDLE_SIGN_OUT;
  if (!user || user.status !== "active") return "user switched off";
  return null;
}

/** Whether last_seen_at is old enough to be written again. */
export function shouldMarkSeen(lastSeen: Date, now: Date): boolean {
  return now.getTime() - lastSeen.getTime() >= SEEN_EVERY_MS;
}

// ---- Settings -------------------------------------------------------------------------------

export interface Auth0Settings {
  domain: string;
  clientId: string;
  clientSecret: string;
}

export type SignInSettings =
  | { provider: "auth0"; auth0: Auth0Settings; googleDomain: string; sessionSecret: string }
  | { provider: "fake"; googleDomain: string; sessionSecret: string };

export type SettingsResult =
  { ok: true; settings: SignInSettings } | { ok: false; problems: string[] };

/** Deploy contexts where the fake provider may stand in for Auth0. Never production. */
export const FAKE_CONTEXTS: readonly string[] = ["deploy-preview", "dev"];

/**
 * Used only by the fake on deploy previews and local runs when SESSION_SECRET isn't set.
 * Previews hold fictional data only; production never accepts the fake, so never this.
 */
export const PREVIEW_SESSION_SECRET = "preview-only-session-secret-fictional-data-0000";

const AUTH0_KEYS = ["AUTH0_DOMAIN", "AUTH0_CLIENT_ID", "AUTH0_CLIENT_SECRET"] as const;

type Env = Readonly<Record<string, string | undefined>>;

/**
 * Reads the sign-in settings for a deploy context. Production and branch deploys need every
 * Auth0 setting, GOOGLE_DOMAIN and SESSION_SECRET, all well-formed; anything missing or wrong
 * gives problems, and the caller refuses every sign-in and protected request. Deploy previews
 * and local runs use the fake provider while no Auth0 setting is present.
 */
export function readSignInSettings(env: Env, deployContext: string): SettingsResult {
  const val = (k: string) => env[k]?.trim() || undefined;
  const problems: string[] = [];
  const fakeAllowed = FAKE_CONTEXTS.includes(deployContext);
  const useFake = fakeAllowed && AUTH0_KEYS.every((k) => !val(k));

  const googleDomain = val("GOOGLE_DOMAIN")?.toLowerCase() ?? (useFake ? "abbella.com" : "");
  if (!googleDomain) problems.push("GOOGLE_DOMAIN is missing");
  else if (!DOMAIN.test(googleDomain)) problems.push("GOOGLE_DOMAIN is not a domain");

  const sessionSecret = val("SESSION_SECRET") ?? (useFake ? PREVIEW_SESSION_SECRET : "");
  if (!sessionSecret) problems.push("SESSION_SECRET is missing");
  else if (sessionSecret.length < 32) problems.push("SESSION_SECRET is shorter than 32 characters");
  else if (sessionSecret === PREVIEW_SESSION_SECRET && !useFake)
    problems.push("SESSION_SECRET is the preview secret");

  if (useFake) {
    return problems.length
      ? { ok: false, problems }
      : { ok: true, settings: { provider: "fake", googleDomain, sessionSecret } };
  }

  for (const k of AUTH0_KEYS) if (!val(k)) problems.push(`${k} is missing`);
  const domain = val("AUTH0_DOMAIN")?.toLowerCase();
  if (domain && !DOMAIN.test(domain)) problems.push("AUTH0_DOMAIN is not a domain");
  const clientSecret = val("AUTH0_CLIENT_SECRET");
  if (clientSecret && clientSecret.length < 32)
    problems.push("AUTH0_CLIENT_SECRET is shorter than 32 characters");

  if (problems.length) return { ok: false, problems };
  return {
    ok: true,
    settings: {
      provider: "auth0",
      auth0: {
        domain: domain as string,
        clientId: val("AUTH0_CLIENT_ID") as string,
        clientSecret: clientSecret as string,
      },
      googleDomain,
      sessionSecret,
    },
  };
}

// ---- Sign out when idle (brief 1E-15, mockup v71) -------------------------------------------------

/**
 * Like a bank: after the company's idle minutes a warning counts down; Stay signed in keeps the
 * session, and when it runs out the session ends and the person signs in the usual way, back on
 * the same page with anything typed still there. The 12-hour and 7-day ends above stay.
 */
export interface IdleSettings {
  officeMinutes: number;
  warnSeconds: number;
  fieldMinutes: number;
}
export const IDLE_SETTING = "idle_sign_out";
/**
 * Brief 1E-30 (DECISIONS 10/31): each app's idle minutes are any whole number typed in, 0 to
 * 1440 (a day); 0 means no idle sign-out (the session's own length still ends it).
 */
export const IDLE_MINUTES = [0, 1440] as const;
export const WARN_CHOICES = [30, 60, 120, 300] as const;
export const DEFAULT_IDLE: IdleSettings = { officeMinutes: 30, warnSeconds: 60, fieldMinutes: 30 };

const oneOf = (list: readonly number[], v: unknown, fallback: number) =>
  list.includes(v as number) ? (v as number) : fallback;
const isMinutes = (v: unknown): v is number =>
  typeof v === "number" && Number.isInteger(v) && v >= IDLE_MINUTES[0] && v <= IDLE_MINUTES[1];

/** The company's idle settings as saved, else the default. */
export function idleSettings(value: unknown): IdleSettings {
  const v = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  return {
    officeMinutes: isMinutes(v.officeMinutes) ? v.officeMinutes : DEFAULT_IDLE.officeMinutes,
    warnSeconds: oneOf(WARN_CHOICES, v.warnSeconds, DEFAULT_IDLE.warnSeconds),
    fieldMinutes: isMinutes(v.fieldMinutes) ? v.fieldMinutes : DEFAULT_IDLE.fieldMinutes,
  };
}

/** Reads a change from Settings: any of the three; minutes are whole numbers, 0 to 1440. */
export function readIdleSettings(
  input: unknown,
  had: IdleSettings,
): { ok: true; value: IdleSettings } | { ok: false; error: string } {
  const b = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  const num = (x: unknown) => (typeof x === "string" && x.trim() ? Number(x) : x);
  const out = { ...had };
  if (b.officeMinutes !== undefined) {
    const n = num(b.officeMinutes);
    if (!isMinutes(n))
      return { ok: false, error: "Office app: whole minutes, 0 to 1440 (0 for no idle sign-out)." };
    out.officeMinutes = n;
  }
  if (b.warnSeconds !== undefined) {
    const n = num(b.warnSeconds);
    if (!(WARN_CHOICES as readonly unknown[]).includes(n))
      return { ok: false, error: "Warn 30 seconds, 1, 2 or 5 minutes before signing out." };
    out.warnSeconds = n as number;
  }
  if (b.fieldMinutes !== undefined) {
    const n = num(b.fieldMinutes);
    if (!isMinutes(n))
      return { ok: false, error: "Field app: whole minutes, 0 to 1440 (0 for no idle sign-out)." };
    out.fieldMinutes = n;
  }
  return { ok: true, value: out };
}

/** The minutes idle before the warning: the field app has its own. */
export const idleMinutesFor = (app: "office" | "field", s: IdleSettings) =>
  app === "field" ? s.fieldMinutes : s.officeMinutes;

/** "30 seconds", "1 minute", "2 minutes". */
export const warnWords = (seconds: number) =>
  seconds < 60 ? `${seconds} seconds` : `${seconds / 60} minute${seconds === 60 ? "" : "s"}`;

/** The countdown: 0:59, 4:00. */
export const countdown = (seconds: number) => {
  const n = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

/**
 * Where the app stands, from the last activity in any of its tabs: active; the warning with the
 * seconds left; or signed out once the warning has run out. With 0 minutes, always active.
 */
export function idlePhase(
  lastActivity: number,
  now: number,
  idleMs: number,
  warnMs: number,
): { phase: "active" } | { phase: "warning"; secondsLeft: number } | { phase: "out" } {
  if (idleMs <= 0) return { phase: "active" };
  const idle = now - lastActivity;
  if (idle < idleMs) return { phase: "active" };
  if (idle < idleMs + warnMs)
    return { phase: "warning", secondsLeft: (idleMs + warnMs - idle) / 1000 };
  return { phase: "out" };
}

/** The history's events for the idle sign-out: the warning, Stay signed in, the sign-out. */
export const IDLE_EVENTS = ["idle_warned", "idle_stayed", "idle_signed_out"] as const;
export type IdleEvent = (typeof IDLE_EVENTS)[number];

// ---- The server's idle sign-out (brief 1E-19c) ---------------------------------------------------

/**
 * The browser's countdown is the warning; the server ends the session on the next request once it
 * has been idle past the company's minutes for its app plus the warning, so a closed tab or browser
 * signs out too. In the history as idle_sign_out.
 */
export const IDLE_SIGN_OUT = "idle_sign_out";

export type AppKind = "office" | "field";
/** The app a request comes from: the shell sends it on every request. */
export const APP_HEADER = "x-fc-app";
/** Set on requests the app makes on its own (keeping a page current, the warning itself). */
export const BACKGROUND_HEADER = "x-fc-background";

/** Requests that only keep a page current, whoever sends them: never activity. */
const POLLING = ["GET /api/inbox/changes", "GET /api/inbox/counts"];

/**
 * Whether a request counts as the person doing something. Polling that only keeps a page alive
 * doesn't: the live inbox's questions, and anything the app marks as its own.
 */
export function countsAsActivity(req: {
  method: string;
  path: string;
  background: boolean;
}): boolean {
  if (req.background) return false;
  return !POLLING.includes(`${req.method.toUpperCase()} ${req.path}`);
}

/** The app a session is in: what the request says, else the field app's own addresses, else as it was. */
export function appOf(header: string | null, path: string, had: AppKind): AppKind {
  if (header === "field" || header === "office") return header;
  if (path === "/api/field" || path.startsWith("/api/field/")) return "field";
  return had;
}

/**
 * Where the adapters are fakes, a company's idle setting may carry testSeconds, which the walks
 * use in place of the minutes (1 to 600). Never read elsewhere; Settings shows minutes only.
 */
export function idleTestSeconds(value: unknown): number | null {
  const v = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  const n = v.testSeconds;
  return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 600 ? n : null;
}

/**
 * How long a session may sit idle before the server ends it: the company's minutes for its app
 * (or the walks' test seconds) plus the warning, plus SEEN_EVERY_MS, since the last activity is
 * written at most that often and the browser's countdown should always run out first.
 */
export function idleLimitMs(
  s: IdleSettings,
  app: AppKind,
  testSeconds: number | null = null,
): number | undefined {
  // 0 minutes: no idle sign-out; the 12-hour and 7-day ends still apply (brief 1E-30).
  if (testSeconds === null && idleMinutesFor(app, s) === 0) return undefined;
  const idle = testSeconds !== null ? testSeconds * 1000 : idleMinutesFor(app, s) * 60_000;
  return idle + s.warnSeconds * 1000 + SEEN_EVERY_MS;
}
